<template>
  <div class="sync-page">
    <!-- Header with Logo and Title -->
    <div class="sync-header d-flex align-items-center mb-4">
      <img
        src="@/assets/images/logo/logo.png"
        alt="Logo"
        class="sync-logo mr-3"
      >
      <div>
        <h2 class="mb-0">
          Gestion des Synchronisations
        </h2>
        <small class="text-muted">{{ syncSubtitle }}</small>
      </div>
    </div>

    <div
      v-if="globalStrategy"
      class="sync-content mt-4"
    >
      <!-- Toolbar: Search and Refresh -->
      <div class="sync-toolbar d-flex justify-content-between align-items-center mb-4 bg-white p-2 rounded shadow-sm">
        <div class="d-flex flex-grow-1 mr-2">
          <b-form-input
            v-model="searchQuery"
            :placeholder="searchPlaceholder"
            class="mr-2"
            type="search"
          />
          <b-form-select
            v-if="globalStrategy !== 'supplier_governorate'"
            v-model="statusFilter"
            :options="statusFilterOptions"
            style="max-width: 250px"
          />
        </div>
        <b-button
          variant="danger"
          size="lg"
          class="px-4"
          @click="fetchAllData"
        >
          <b-icon-arrow-clockwise class="mr-1" />
          Rafraîchir
        </b-button>
      </div>

      <!-- TABLE: Per Supplier -->
      <div v-if="globalStrategy === 'supplier'">
        <b-table
          :items="filteredSupplierMatrix"
          :fields="supplierTableFields"
          responsive
          hover
          class="sync-table bg-white shadow-sm rounded"
        >
          <template #cell(username)="row">
            <div class="d-flex align-items-center">
              <b-avatar
                size="32"
                :text="row.item.username.charAt(0).toUpperCase()"
                variant="light"
                class="mr-2"
              />
              <span class="font-weight-bold text-dark">{{ row.item.username }}</span>
            </div>
          </template>
          <template #cell(statut)="row">
            <b-badge :variant="row.item.outsourcing_id ? 'success' : 'secondary'">
              {{ row.item.outsourcing_id ? 'Synchronisé' : 'Non synchronisé' }}
            </b-badge>
          </template>
          <template #cell(societes)="row">
            <b-badge
              v-if="row.item.outsourcing_id"
              variant="info"
              class="px-3"
            >
              {{ getOutsourcingName(row.item.outsourcing_id) }}
            </b-badge>
            <span
              v-else
              class="text-muted"
            >-</span>
          </template>
          <template #cell(actions)="row">
            <b-button
              variant="outline-danger"
              size="sm"
              style="padding: 0.2rem 0.5rem"
              class="mr-1 bg-white"
              @click="openEditSyncModal(row.item, 'supplier')"
            >
              <b-icon-pencil />
            </b-button>
            <b-button
              variant="outline-danger"
              size="sm"
              style="padding: 0.2rem 0.5rem"
              class="bg-white"
              @click="removeSyncMapping(row.item, 'supplier')"
            >
              <b-icon-x-circle />
            </b-button>
          </template>
        </b-table>
      </div>

      <!-- TABLE: Per Governorate -->
      <div v-if="globalStrategy === 'governorate'">
        <div class="bulk-sync-panel bg-white shadow-sm rounded mb-3">
          <div class="bulk-sync-summary">
            <strong>{{ selectedGovernorateIds.length }}</strong>
            gouvernorat{{ selectedGovernorateIds.length > 1 ? 's' : '' }} sélectionné{{ selectedGovernorateIds.length > 1 ? 's' : '' }}
          </div>
          <div class="bulk-sync-controls">
            <v-select
              v-model="bulkGovernorateOutsourcingId"
              :options="outsourcingCompanyBulkOptions"
              :reduce="c => c.value"
              label="text"
              placeholder="Société logistique externe..."
              class="bulk-company-select"
              :disabled="isBulkSaving"
            />
            <b-button
              variant="outline-secondary"
              :disabled="isBulkSaving || filteredGovernorateMatrix.length === 0"
              @click="selectVisibleGovernorates"
            >
              Tout sélectionner
            </b-button>
            <b-button
              variant="outline-secondary"
              :disabled="isBulkSaving || selectedGovernorateIds.length === 0"
              @click="clearGovernorateSelection"
            >
              Vider
            </b-button>
            <b-button
              variant="success"
              :disabled="!canBulkSyncGovernorates"
              @click="bulkSyncSelectedGovernorates"
            >
              Appliquer
            </b-button>
          </div>
        </div>

        <b-table
          :items="filteredGovernorateMatrix"
          :fields="governorateTableFields"
          responsive
          hover
          class="sync-table bg-white shadow-sm rounded"
        >
          <template #head(selected)>
            <b-form-checkbox
              :checked="allVisibleGovernoratesSelected"
              :indeterminate="isVisibleGovernorateSelectionIndeterminate"
              :disabled="isBulkSaving || filteredGovernorateMatrix.length === 0"
              @change="toggleVisibleGovernoratesSelection"
            />
          </template>
          <template #cell(selected)="row">
            <b-form-checkbox
              v-model="selectedGovernorateIds"
              :value="row.item.id"
              :disabled="isBulkSaving"
            />
          </template>
          <template #cell(name)="row">
            <div class="d-flex align-items-center">
              <b-avatar
                size="32"
                :text="row.item.name.charAt(0)"
                variant="light-primary"
                class="mr-2"
              />
              <span class="font-weight-bold text-dark">{{ row.item.name }}</span>
            </div>
          </template>
          <template #cell(statut)="row">
            <b-badge :variant="row.item.outsourcing_id ? 'success' : 'secondary'">
              {{ row.item.outsourcing_id ? 'Synchronisé' : 'Non synchronisé' }}
            </b-badge>
          </template>
          <template #cell(societes)="row">
            <b-badge
              v-if="row.item.outsourcing_id"
              variant="info"
              class="px-3"
            >
              {{ getOutsourcingName(row.item.outsourcing_id) }}
            </b-badge>
            <span
              v-else
              class="text-muted"
            >-</span>
          </template>
          <template #cell(actions)="row">
            <b-button
              variant="outline-danger"
              size="sm"
              style="padding: 0.2rem 0.5rem"
              class="mr-1 bg-white"
              @click="openEditSyncModal(row.item, 'governorate')"
            >
              <b-icon-pencil />
            </b-button>
            <b-button
              variant="outline-danger"
              size="sm"
              style="padding: 0.2rem 0.5rem"
              class="bg-white"
              @click="removeSyncMapping(row.item, 'governorate')"
            >
              <b-icon-x-circle />
            </b-button>
          </template>
        </b-table>
      </div>

      <!-- TABLE: Hybrid (Supplier Centric) -->
      <div v-if="globalStrategy === 'supplier_governorate'">
        <b-table
          :items="filteredHybridSupplierMatrix"
          :fields="hybridSupplierTableFields"
          responsive
          hover
          class="sync-table bg-white shadow-sm rounded"
        >
          <template #cell(username)="row">
            <div class="d-flex align-items-center">
              <b-avatar
                size="32"
                :text="row.item.username.charAt(0).toUpperCase()"
                variant="light-warning"
                class="mr-2"
              />
              <span class="font-weight-bold text-dark">{{ row.item.username }}</span>
            </div>
          </template>
          <template #cell(sync_status)="row">
            <b-progress
              :value="row.item.sync_count"
              :max="governorates.length"
              variant="success"
              height="10px"
              class="mt-1"
            />
            <small class="text-muted">{{ row.item.sync_count }} / {{ governorates.length }} gouvernorats synchronisés</small>
          </template>
          <template #cell(actions)="row">
            <b-button
              variant="warning"
              size="sm"
              class="text-white"
              @click="openHybridManagement(row.item)"
            >
              <b-icon-gear-fill class="mr-1" />
              Gérer les Mappings
            </b-button>
          </template>
        </b-table>
      </div>

    </div>

    <!-- Mapping Modal -->
    <b-modal
      id="mapping-modal"
      title="Nouveau Mapping de Synchronisation"
      ok-title="Enregistrer"
      cancel-title="Annuler"
      @ok="saveMapping"
    >
      <b-form-group label="Transporteur Externe">
        <v-select
          v-model="selectedMappingCompany"
          :options="outsourcingCompaniesOptions"
          :reduce="c => c.company"
          label="text"
          placeholder="Sélectionner une société..."
          @input="onMappingCompanyChange"
        />
      </b-form-group>

      <div v-if="selectedMappingCompany">
        <p class="text-muted small">
          Mode d'intégration configuré : <strong>{{ getModeLabel(selectedMappingCompany.integration_mode) }}</strong>
        </p>

        <!-- Supplier Selection -->
        <b-form-group
          v-if="needsSupplier"
          label="Fournisseur"
        >
          <v-select
            v-model="selectedMappingSupplier"
            :options="suppliers"
            label="username"
            placeholder="Sélectionner un fournisseur..."
          />
        </b-form-group>

        <!-- Governorate Selection -->
        <b-form-group
          v-if="needsGovernorate"
          label="Gouvernorat"
        >
          <v-select
            v-model="selectedMappingGovernorate"
            :options="governorates"
            label="name"
            placeholder="Sélectionner un gouvernorat..."
          />
        </b-form-group>

      </div>
    </b-modal>

    <!-- Mode Change Confirmation Modal -->
    <b-modal
      id="mode-change-modal"
      title="Confirmation de Changement de Mode"
      ok-title="Confirmer le changement"
      cancel-title="Annuler"
      ok-variant="danger"
      @ok="confirmModeChange"
      @cancel="cancelModeChange"
      @hidden="cancelModeChange"
    >
      <div v-if="modeChangePreview">
        <p>
          Vous êtes sur le point de passer en mode <strong>{{ getModeLabel(pendingModeChange) }}</strong>.
        </p>

        <b-alert
          v-if="modeChangePreview.to_delete > 0"
          variant="warning"
          show
        >
          <strong>⚠️ Attention :</strong> {{ modeChangePreview.to_delete }} mappings incompatibles seront supprimés :
          <ul class="mb-0 mt-2">
            <li
              v-for="detail in modeChangePreview.affected_details"
              :key="detail"
            >
              {{ detail }}
            </li>
          </ul>
        </b-alert>

        <b-alert
          v-else
          variant="success"
          show
        >
          ✅ Aucun mapping ne sera supprimé.
        </b-alert>

        <p class="text-muted small mb-2">
          <strong>Statistiques actuelles :</strong>
        </p>
        <ul class="small">
          <li>Mappings par fournisseur : {{ modeChangePreview.current_counts.supplier_only }}</li>
          <li>Mappings par gouvernorat : {{ modeChangePreview.current_counts.governorate_only }}</li>
          <li>Mappings hybrides : {{ modeChangePreview.current_counts.hybrid }}</li>
        </ul>

        <b-form-checkbox
          v-if="modeChangePreview.to_delete > 0"
          v-model="cleanupOnModeChange"
          class="mt-3"
        >
          Supprimer automatiquement les {{ modeChangePreview.to_delete }} mappings incompatibles
        </b-form-checkbox>
      </div>
      <div
        v-else
        class="text-center py-3"
      >
        <b-spinner />
        <p class="mt-2 text-muted">
          Analyse en cours...
        </p>
      </div>
    </b-modal>

    <!-- Hybrid Management Modal (THE NEW BIG ONE) -->
    <b-modal
      id="hybrid-management-modal"
      size="xl"
      scrollable
      ok-only
      ok-title="Fermer"
      ok-variant="outline-secondary"
      :title="'Gestion des Synchronisations : ' + (selectedHybridSupplier ? selectedHybridSupplier.username : '')"
    >
      <div v-if="selectedHybridSupplier">
        <div class="alert alert-light border mb-4">
          <h5 class="alert-heading text-primary">
            Assignation des gouvernorats
          </h5>
          <p class="mb-0 small">
            Sélectionnez la société de livraison pour chaque gouvernorat pour le fournisseur <strong>{{ selectedHybridSupplier.username }}</strong>.
          </p>
        </div>

        <b-table
          :items="hybridGovernorateMatrix"
          :fields="hybridGovFields"
          striped
          hover
          small
          responsive
        >
          <template #cell(governorate)="row">
            <strong>{{ row.item.name }}</strong>
          </template>
          <template #cell(outsourcing)="row">
            <v-select
              v-model="row.item.outsourcing_id"
              :options="outsourcingCompanyOptions"
              :reduce="c => c.value"
              label="text"
              placeholder="Sélectionner..."
              class="bg-white"
              @input="(val) => saveHybridMapping(row.item, val)"
            />
          </template>
        </b-table>
      </div>
    </b-modal>

    <!-- Edit Sync Modal -->

    <b-modal
      id="edit-sync-modal"
      :title="editSyncModalTitle"
      ok-title="Synchroniser"
      cancel-title="Annuler"
      ok-variant="success"
      @ok="confirmEditSync"
    >
      <b-form-group :label="editingSyncType === 'governorate' ? 'Gouvernorat :' : 'Fournisseur :'">
        <p class="mb-0 font-weight-bold">
          {{ editingSyncType === 'governorate' ? (editingSyncItem && editingSyncItem.name) : (editingSyncItem && editingSyncItem.username) }}
        </p>
      </b-form-group>

      <b-form-group label="Société logistique externe">
        <v-select
          v-model="editingOutsourcingId"
          :options="outsourcingCompaniesOptions"
          :reduce="c => c.value"
          label="text"
          placeholder="Sélectionner une société..."
        />
      </b-form-group>

      <p class="text-success small mt-3">
        <strong>Cette action mettra à jour la synchronisation actuelle.</strong>
      </p>
    </b-modal>

    <!-- Loading Overlay -->
    <b-overlay
      :show="isLoading"
      no-wrap
      fixed
    />

    <!-- Toast for Feedback -->
    <b-toast
      v-if="alertVisible"
      :variant="alertVariant"
      :auto-hide-delay="3500"
      solid
      toaster="b-toaster-top-right"
      @hidden="alertVisible = false"
    >
      {{ alertMessage }}
    </b-toast>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import {
  BTable, BBadge, BButton, BModal, BFormGroup, BFormInput, BFormSelect, BOverlay, BToast,
  BIconXCircle, BFormCheckbox, BIconPencil, BAvatar, BProgress, BIconGearFill,
} from 'bootstrap-vue'
import axios from 'axios'

export default {
  name: 'SynchronizationManagement',
  components: {
    vSelect,
    BTable,
    BBadge,
    BButton,
    BModal,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BOverlay,
    BToast,
    BIconXCircle,
    BFormCheckbox,
    BIconPencil,
    BAvatar,
    BProgress,
    BIconGearFill,
  },
  data() {
    return {
      governorates: [],
      suppliers: [],
      outsourcingCompanies: [],
      allSyncs: [],
      isLoading: false,
      alertVisible: false,
      alertVariant: '',
      alertMessage: '',
      searchQuery: '',
      mappingTypeFilter: 'all',
      globalStrategy: 'governorate',
      globalStrategyOptions: [
        { value: 'supplier', text: 'Par Fournisseur' },
        { value: 'governorate', text: 'Par Gouvernorat' },
        { value: 'supplier_governorate', text: 'Par Fournisseur et Gouvernorat (Hybride)' },
      ],

      // New table fields for redesigned UI
      supplierTableFields: [
        { key: 'username', label: 'FOURNISSEUR' },
        { key: 'statut', label: 'STATUT' },
        { key: 'societes', label: 'SOCIÉTÉS SYNCHRONISÉES' },
        { key: 'actions', label: 'ACTIONS', class: 'text-right' },
      ],
      governorateTableFields: [
        {
          key: 'selected', label: '', class: 'select-column',
        },
        { key: 'name', label: 'GOUVERNORAT' },
        { key: 'statut', label: 'STATUT' },
        { key: 'societes', label: 'SOCIÉTÉS SYNCHRONISÉES' },
        { key: 'actions', label: 'ACTIONS', class: 'text-right' },
      ],
      supplierMappingFields: [
        { key: 'username', label: 'Fournisseur' },
        { key: 'outsourcing_company', label: 'Société de Livraison' },
      ],

      // Modal data
      selectedMappingCompany: null,
      selectedMappingSupplier: null,
      selectedMappingGovernorate: null,
      selectedMappingSyncType: 'all',

      // Edit sync modal data
      editingSyncItem: null,
      editingSyncType: null, // 'supplier' or 'governorate'
      editingOutsourcingId: null,
      selectedGovernorateIds: [],
      bulkGovernorateOutsourcingId: null,
      isBulkSaving: false,

      integrationModeOptions: [
        { value: 'supplier', text: 'Par Fournisseur' },
        { value: 'governorate', text: 'Par Gouvernorat' },
        { value: 'supplier_governorate', text: 'Par Fournisseur et Gouvernorat' },
      ],

      companyTableFields: [
        { key: 'name', label: 'Transporteur' },
        { key: 'is_active', label: 'Statut' },
      ],

      syncTableFields: [
        { key: 'type', label: 'Règle' },
        { key: 'target', label: 'Cible' },
        { key: 'company', label: 'Transporteur' },
        { key: 'actions', label: 'Actions', class: 'text-center' },
      ],

      mappingTypeOptions: [
        { value: 'all', text: 'Tous les mappings' },
        { value: 'supplier', text: 'Fournisseurs uniquement' },
        { value: 'governorate', text: 'Gouvernorats uniquement' },
        { value: 'hybrid', text: 'Hybrides (Fourn + Gov)' },
      ],
      syncTypeOptions: [
        { value: 'all', text: 'Tous (Simple + Échange)' },
        { value: 'simple', text: 'Simple Uniquement' },
        { value: 'exchange', text: 'Échange Uniquement' },
      ],
      statusFilter: 'all',
      statusFilterOptions: [
        { value: 'all', text: 'Tous les statuts' },
        { value: 'synced', text: 'Synchronisé' },
        { value: 'unsynced', text: 'Non synchronisé' },
      ],
      governorateMappingFields: [
        { key: 'name', label: 'Gouvernorat' },
        { key: 'outsourcing_company', label: 'Société de Livraison' },
      ],

      // Hybrid Redesign State
      selectedHybridSupplier: null,
      hybridSupplierTableFields: [
        { key: 'username', label: 'FOURNISSEUR' },
        { key: 'sync_status', label: 'ÉTAT DE SYNCHRONISATION' },
        { key: 'actions', label: 'ACTIONS', class: 'text-right' },
      ],
      hybridGovFields: [
        { key: 'governorate', label: 'GOUVERNORAT' },
        { key: 'outsourcing', label: 'SOCIÉTÉ DE LIVRAISON EXTERNE' },
      ],
      pendingModeChange: null,
      modeChangePreview: null,
      cleanupOnModeChange: true,
      previousGlobalStrategy: 'all',
    }
  },
  computed: {
    editSyncModalTitle() {
      if (this.editingSyncType === 'governorate') {
        return `Synchronisation du gouvernorat : ${this.editingSyncItem ? this.editingSyncItem.name : ''}`
      }
      return `Synchronisation du fournisseur : ${this.editingSyncItem ? this.editingSyncItem.username : ''}`
    },
    needsSupplier() {
      return ['supplier', 'supplier_governorate'].includes(this.globalStrategy)
    },
    needsGovernorate() {
      return ['governorate', 'supplier_governorate'].includes(this.globalStrategy)
    },
    searchPlaceholder() {
      if (this.globalStrategy === 'supplier') return 'Rechercher un fournisseur...'
      if (this.globalStrategy === 'governorate') return 'Rechercher un gouvernorat...'
      return 'Rechercher...'
    },
    syncSubtitle() {
      if (this.globalStrategy === 'supplier') return 'Gérez la synchronisation des fournisseurs avec les sociétés logistiques externes.'
      if (this.globalStrategy === 'governorate') return 'Gérez la synchronisation des gouvernorats avec les sociétés logistiques externes.'
      return 'Gérez la stratégie d\'intégration et le routage des colis vers les transporteurs externes.'
    },
    filteredSyncs() {
      let filtered = this.allSyncs

      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase()
        filtered = filtered.filter(s => (s.supplier_username && s.supplier_username.toLowerCase().includes(q))
          || (s.governorate_name && s.governorate_name.toLowerCase().includes(q))
          || (s.outsourcing_company_name && s.outsourcing_company_name.toLowerCase().includes(q)))
      }

      if (this.mappingTypeFilter === 'supplier') {
        filtered = filtered.filter(s => s.supplier && !s.governorate)
      } else if (this.mappingTypeFilter === 'governorate') {
        filtered = filtered.filter(s => s.governorate && !s.supplier)
      } else if (this.mappingTypeFilter === 'hybrid') {
        filtered = filtered.filter(s => s.supplier && s.governorate)
      }

      return filtered
    },
    supplierMatrix() {
      return this.suppliers.map(s => {
        const sync = this.allSyncs.find(sItem => Number(sItem.supplier) === Number(s.id) && !sItem.governorate)
        return {
          id: s.id,
          username: s.username,
          outsourcing_id: sync ? sync.outsourcing_company : null,
        }
      })
    },
    filteredSupplierMatrix() {
      let matrix = this.supplierMatrix

      // Filter by Search Query
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase()
        matrix = matrix.filter(s => s.username.toLowerCase().includes(q))
      }

      // Filter by Status
      if (this.statusFilter === 'synced') {
        matrix = matrix.filter(s => s.outsourcing_id)
      } else if (this.statusFilter === 'unsynced') {
        matrix = matrix.filter(s => !s.outsourcing_id)
      }

      return matrix
    },
    outsourcingCompanyOptions() {
      const opts = [
        { value: null, text: '--- Aucune synchronisation ---' },
      ]
      this.outsourcingCompanies.forEach(c => {
        opts.push({ value: c.id, text: c.display_name || c.name })
      })
      return opts
    },
    outsourcingCompaniesOptions() {
      return this.outsourcingCompanies.map(c => ({
        value: c.id,
        company: c,
        text: c.display_name || c.name,
      }))
    },
    outsourcingCompanyBulkOptions() {
      return this.outsourcingCompanies.map(c => ({
        value: c.id,
        text: c.display_name || c.name,
      }))
    },
    governorateMatrix() {
      return this.governorates.map(g => {
        const sync = this.allSyncs.find(gItem => Number(gItem.governorate) === Number(g.id) && !gItem.supplier)
        return {
          id: g.id,
          name: g.name,
          outsourcing_id: sync ? sync.outsourcing_company : null,
        }
      })
    },
    filteredGovernorateMatrix() {
      let matrix = this.governorateMatrix
      // Filter by Search Query
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase()
        matrix = matrix.filter(g => g.name.toLowerCase().includes(q))
      }
      // Filter by Status
      if (this.statusFilter === 'synced') {
        matrix = matrix.filter(g => g.outsourcing_id)
      } else if (this.statusFilter === 'unsynced') {
        matrix = matrix.filter(g => !g.outsourcing_id)
      }
      return matrix
    },
    filteredGovernorateIds() {
      return this.filteredGovernorateMatrix.map(governorate => governorate.id)
    },
    allVisibleGovernoratesSelected() {
      return this.filteredGovernorateIds.length > 0
        && this.filteredGovernorateIds.every(id => this.selectedGovernorateIds.includes(id))
    },
    isVisibleGovernorateSelectionIndeterminate() {
      const selectedVisibleCount = this.filteredGovernorateIds.filter(id => this.selectedGovernorateIds.includes(id)).length
      return selectedVisibleCount > 0 && selectedVisibleCount < this.filteredGovernorateIds.length
    },
    canBulkSyncGovernorates() {
      return !this.isBulkSaving && this.selectedGovernorateIds.length > 0 && Boolean(this.bulkGovernorateOutsourcingId)
    },
    hybridSupplierMatrix() {
      return this.suppliers.map(s => {
        const supplierSyncs = this.allSyncs.filter(sync => Number(sync.supplier) === Number(s.id) && sync.governorate)
        return {
          ...s,
          sync_count: supplierSyncs.length,
        }
      })
    },
    filteredHybridSupplierMatrix() {
      if (!this.searchQuery) return this.hybridSupplierMatrix
      const q = this.searchQuery.toLowerCase()
      return this.hybridSupplierMatrix.filter(s => s.username.toLowerCase().includes(q))
    },
    hybridGovernorateMatrix() {
      if (!this.selectedHybridSupplier) return []
      return this.governorates.map(g => {
        const sync = this.allSyncs.find(s => Number(s.supplier) === Number(this.selectedHybridSupplier.id) && Number(s.governorate) === Number(g.id))
        return {
          id: g.id,
          name: g.name,
          outsourcing_id: sync ? sync.outsourcing_company : null,
        }
      })
    },
  },
  created() {
    this.fetchAllData()
  },
  methods: {
    async fetchAllData() {
      this.isLoading = true
      try {
        const [companies, govs, suppliers, syncs, syncModeResponse] = await Promise.all([
          axios.get('/api/companies/outsourcing-company/'),
          axios.get('/api/governorates/'),
          axios.get('/api/auth/users/suppliers/'),
          axios.get('/api/companies/all-syncs/'),
          axios.get('/api/companies/company/sync-mode/').catch(() => ({ data: { sync_mode: 'governorate' } })),
        ])

        this.outsourcingCompanies = companies.data.results || companies.data || []
        this.governorates = govs.data.results || govs.data || []
        this.suppliers = suppliers.data.results || suppliers.data || []
        this.allSyncs = syncs.data.results || syncs.data || []

        // Get global sync mode from Company
        this.globalStrategy = syncModeResponse.data?.sync_mode || 'governorate'
        this.previousGlobalStrategy = this.globalStrategy
      } catch (error) {
        this.showAlert('danger', 'Erreur lors du chargement des données')
      } finally {
        this.isLoading = false
      }
    },

    getModeLabel(mode) {
      // Check integration mode options first
      let opt = this.integrationModeOptions.find(o => o.value === mode)
      if (opt) return opt.text

      // Then check global strategy options
      opt = this.globalStrategyOptions.find(o => o.value === mode)
      return opt ? opt.text : mode
    },

    getSyncTypeLabel(type) {
      const opt = this.syncTypeOptions.find(o => o.value === type)
      return opt ? opt.text : type
    },

    getMappingBadgeVariant(sync) {
      if (sync.supplier && sync.governorate) return 'warning'
      if (sync.supplier) return 'primary'
      return 'secondary'
    },

    getMappingTypeText(sync) {
      if (sync.supplier && sync.governorate) return 'Hybride'
      if (sync.supplier) return 'Fournisseur'
      return 'Gouvernorat'
    },

    async saveCompanySettings(company) {
      try {
        this.isLoading = true
        await axios.patch(`/api/companies/outsourcing-company/update/${company.id}/`, {
          is_active: company.is_active,
        })
        this.showAlert('success', 'Statut enregistré')
      } catch (error) {
        this.showAlert('danger', 'Erreur lors de l\'enregistrement')
      } finally {
        this.isLoading = false
      }
    },

    async saveGlobalStrategy() {
      // This method saves the sync mode to Company
      try {
        this.isLoading = true
        await axios.post('/api/companies/company/sync-mode/', {
          sync_mode: this.globalStrategy,
        })
        this.previousGlobalStrategy = this.globalStrategy
        await this.fetchAllData()
        this.showAlert('success', 'Mode de synchronisation mis à jour avec succès')
      } catch (error) {
        this.globalStrategy = this.previousGlobalStrategy
        this.showAlert('danger', 'Erreur lors de la mise à jour du mode')
      } finally {
        this.isLoading = false
      }
    },

    async onGlobalStrategyChange() {
      // Directly save the new mode to Company
      await this.saveGlobalStrategy()
    },

    async confirmModeChange() {
      await this.saveGlobalStrategy()
      this.pendingModeChange = null
      this.modeChangePreview = null
    },

    cancelModeChange() {
      // Revert to previous mode
      this.globalStrategy = this.previousGlobalStrategy
      this.pendingModeChange = null
      this.modeChangePreview = null
    },

    openNewMappingModal(mode = 'all') {
      this.selectedMappingCompany = null
      this.selectedMappingSupplier = null
      this.selectedMappingGovernorate = null
      this.selectedMappingSyncType = 'all'

      if (mode !== 'all') {
        this.mappingTypeFilter = mode
      }

      this.$bvModal.show('mapping-modal')
    },

    onMappingCompanyChange() {
      this.selectedMappingSupplier = null
      this.selectedMappingGovernorate = null
    },

    async saveMapping() {
      if (!this.selectedMappingCompany) return

      const payload = {
        outsourcing_company_id: this.selectedMappingCompany.id,
        is_synchronized: true,
        sync_type: this.selectedMappingSyncType,
      }

      if (this.needsSupplier) {
        if (!this.selectedMappingSupplier) {
          this.showAlert('warning', 'Veuillez sélectionner un fournisseur')
          return
        }
        payload.supplier_id = this.selectedMappingSupplier.id
      }

      if (this.needsGovernorate) {
        if (!this.selectedMappingGovernorate) {
          this.showAlert('warning', 'Veuillez sélectionner un gouvernorat')
          return
        }
        payload.governorate_id = this.selectedMappingGovernorate.id
      }

      try {
        this.isLoading = true
        await axios.post('/api/companies/update-supplier-outsourcing/', payload)
        await this.fetchAllData()
        this.showAlert('success', 'Mapping enregistré avec succès')
      } catch (error) {
        this.showAlert('danger', 'Erreur lors de l\'enregistrement du mapping')
      } finally {
        this.isLoading = false
      }
    },

    async deleteMapping(sync) {
      try {
        this.isLoading = true
        await axios.delete(`/api/companies/delete-sync/${sync.id}/`)
        await this.fetchAllData()
        this.showAlert('success', 'Mapping supprimé')
      } catch (error) {
        this.showAlert('danger', 'Erreur lors de la suppression')
      } finally {
        this.isLoading = false
      }
    },

    async quickMap(entity, syncType, companyId, mode) {
      // Validate against current global mode
      if (this.globalStrategy !== 'all') {
        if (this.globalStrategy === 'supplier' && mode !== 'supplier') {
          this.showAlert('warning', 'Mode "Par Fournisseur" actif - seuls les mappings fournisseur sont autorisés')
          await this.fetchAllData() // Reset UI
          return
        }
        if (this.globalStrategy === 'governorate' && mode !== 'governorate') {
          this.showAlert('warning', 'Mode "Par Gouvernorat" actif - seuls les mappings gouvernorat sont autorisés')
          await this.fetchAllData()
          return
        }
        if (this.globalStrategy === 'hybrid' && mode !== 'hybrid') {
          this.showAlert('warning', 'Mode "Hybride" actif - seuls les mappings hybrides sont autorisés')
          await this.fetchAllData()
          return
        }
      }

      if (!companyId) {
        // Find existing sync and delete it
        const existing = this.allSyncs.find(s => {
          if (mode === 'supplier') return s.supplier === entity.id && !s.governorate
          if (mode === 'governorate') return s.governorate === entity.id && !s.supplier
          return false
        })
        if (existing) {
          await this.deleteMapping(existing)
        }
        return
      }

      const payload = {
        outsourcing_company_id: companyId,
        is_synchronized: true,
        sync_type: syncType,
      }

      if (mode === 'supplier') payload.supplier_id = entity.id
      if (mode === 'governorate') payload.governorate_id = entity.id

      try {
        this.isLoading = true
        await axios.post('/api/companies/update-supplier-outsourcing/', payload)
        await this.fetchAllData()
        this.showAlert('success', 'Mise à jour réussie')
      } catch (error) {
        const errorMessage = error.response?.data?.error || error.response?.data?.governorate || error.response?.data?.supplier || 'Erreur lors de la mise à jour'
        this.showAlert('danger', errorMessage)
        await this.fetchAllData() // Reset UI to consistent state
      } finally {
        this.isLoading = false
      }
    },

    // Helper method to get outsourcing company name by ID
    getOutsourcingName(id) {
      const company = this.outsourcingCompanies.find(c => c.id === id)
      return company ? (company.display_name || company.name) : '-'
    },

    // Open edit sync modal
    openEditSyncModal(item, type) {
      this.editingSyncItem = item
      this.editingSyncType = type
      this.editingOutsourcingId = item.outsourcing_id
      this.$bvModal.show('edit-sync-modal')
    },

    // Confirm edit sync and save
    async confirmEditSync() {
      if (!this.editingOutsourcingId) {
        this.showAlert('warning', 'Veuillez sélectionner une société logistique')
        return
      }

      // Use quickMap to save
      await this.quickMap(this.editingSyncItem, 'all', this.editingOutsourcingId, this.editingSyncType)
      this.$bvModal.hide('edit-sync-modal')
      this.editingSyncItem = null
      this.editingSyncType = null
      this.editingOutsourcingId = null
    },

    // Remove sync mapping
    async removeSyncMapping(item, type) {
      const existing = this.allSyncs.find(s => {
        if (type === 'supplier') return Number(s.supplier) === Number(item.id) && !s.governorate
        if (type === 'governorate') return Number(s.governorate) === Number(item.id) && !s.supplier
        return false
      })
      if (existing) {
        await this.deleteMapping(existing)
      }
    },

    selectVisibleGovernorates() {
      const selected = new Set(this.selectedGovernorateIds)
      this.filteredGovernorateIds.forEach(id => selected.add(id))
      this.selectedGovernorateIds = Array.from(selected)
    },

    clearGovernorateSelection() {
      this.selectedGovernorateIds = []
    },

    toggleVisibleGovernoratesSelection(checked) {
      if (checked) {
        this.selectVisibleGovernorates()
        return
      }

      const visibleIds = new Set(this.filteredGovernorateIds)
      this.selectedGovernorateIds = this.selectedGovernorateIds.filter(id => !visibleIds.has(id))
    },

    async bulkSyncSelectedGovernorates() {
      if (!this.canBulkSyncGovernorates) return

      const selectedIds = [...this.selectedGovernorateIds]
      const requests = selectedIds.map(governorateId => axios.post('/api/companies/update-supplier-outsourcing/', {
        governorate_id: governorateId,
        outsourcing_company_id: this.bulkGovernorateOutsourcingId,
        is_synchronized: true,
        sync_type: 'all',
      }))

      try {
        this.isLoading = true
        this.isBulkSaving = true
        await Promise.all(requests)
        await this.fetchAllData()
        this.clearGovernorateSelection()
        this.showAlert('success', `${selectedIds.length} gouvernorat${selectedIds.length > 1 ? 's' : ''} synchronisé${selectedIds.length > 1 ? 's' : ''} avec succès`)
      } catch (error) {
        const message = error.response?.data?.error || 'Erreur lors de la synchronisation multiple'
        this.showAlert('danger', message)
        await this.fetchAllData()
      } finally {
        this.isBulkSaving = false
        this.isLoading = false
      }
    },

    // Open Hybrid Management Modal
    openHybridManagement(supplier) {
      this.selectedHybridSupplier = supplier
      this.$bvModal.show('hybrid-management-modal')
    },

    // Save mapping from Hybrid Modal
    async saveHybridMapping(gov, outsourcingId) {
      if (!this.selectedHybridSupplier) return

      if (!outsourcingId) {
        // Desynchronize
        const sync = this.allSyncs.find(s => Number(s.supplier) === Number(this.selectedHybridSupplier.id) && Number(s.governorate) === Number(gov.id))
        if (sync) {
          await this.deleteMapping(sync)
        }
        return
      }

      // Quick map for hybrid
      await this.quickMapHybrid(this.selectedHybridSupplier, gov, outsourcingId)
    },

    async quickMapHybrid(supplier, gov, companyId) {
      const payload = {
        supplier_id: supplier.id,
        governorate_id: gov.id,
        outsourcing_company_id: companyId,
        is_synchronized: true,
        sync_type: 'all',
      }

      try {
        this.isLoading = true
        await axios.post('/api/companies/update-supplier-outsourcing/', payload)
        await this.fetchAllData()
        this.showAlert('success', 'Synchronisation mise à jour')
      } catch (error) {
        this.showAlert('danger', 'Erreur lors de la mise à jour')
        await this.fetchAllData()
      } finally {
        this.isLoading = false
      }
    },

    showAlert(variant, message) {
      this.alertVisible = true
      this.alertVariant = variant
      this.alertMessage = message
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";
.sync-page {
  padding: 2rem;
}
.sync-header {
  border-bottom: 2px solid #f0f2f5;
  padding-bottom: 1.5rem;
}
.sync-logo {
  height: 50px;
}
.sync-toolbar {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 12px;
}
.sync-table {
  margin-top: 1rem;
}
.bulk-sync-panel {
  align-items: center;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  padding: 1rem 1.25rem;
}
.bulk-sync-summary {
  color: #5e6e82;
  min-width: 180px;
}
.bulk-sync-controls {
  align-items: center;
  display: flex;
  flex: 1;
  gap: 0.75rem;
  justify-content: flex-end;
}
.bulk-company-select {
  max-width: 360px;
  min-width: 260px;
  width: 100%;
}
::v-deep .select-column {
  text-align: center;
  width: 48px;
}
@media (max-width: 992px) {
  .bulk-sync-panel,
  .bulk-sync-controls {
    align-items: stretch;
    flex-direction: column;
  }

  .bulk-company-select {
    max-width: none;
  }
}
</style>
